import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  History, RefreshCw, Trash2, ChevronDown, ChevronRight, ArrowRight
} from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const AdminTradosAgentHistory = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [conversationPage, setConversationPage] = useState(1);
  const [conversationsTotal, setConversationsTotal] = useState(0);

  const fetchConversations = useCallback(async (page = 1) => {
    setConversationsLoading(true);
    try {
      const res = await api.get(`/admin/trados-agent/conversations?page=${page}&per_page=10`);
      setConversations(res.data.conversations || []);
      setConversationsTotal(res.data.total || 0);
      setConversationPage(page);
    } catch (err) { toast.error('Failed to load conversations'); }
    finally { setConversationsLoading(false); }
  }, []);

  useEffect(() => { fetchConversations(1); }, [fetchConversations]);

  const loadConversationDetail = async (sessionId) => {
    if (selectedConversation === sessionId) {
      setSelectedConversation(null);
      setConversationDetail(null);
      return;
    }
    try {
      const res = await api.get(`/admin/trados-agent/conversations/${sessionId}`);
      setConversationDetail(res.data);
      setSelectedConversation(sessionId);
    } catch (err) { toast.error('Failed to load conversation'); }
  };

  const deleteConversation = async (sessionId) => {
    try {
      await api.delete(`/admin/trados-agent/conversations/${sessionId}`);
      toast.success('Conversation deleted');
      setSelectedConversation(null);
      setConversationDetail(null);
      fetchConversations(conversationPage);
    } catch (err) { toast.error('Failed to delete conversation'); }
  };

  const continueConversation = (conv) => {
    if (conversationDetail) {
      navigate('/admin/trados-agent', {
        state: {
          sessionId: conv.session_id,
          messages: conversationDetail.messages.filter(m => m.role !== 'system').map(m => ({
            role: m.role, content: m.content
          })),
        }
      });
    }
  };

  return (
    <AgentLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Conversation History</h3>
          <span className="text-sm text-gray-500">{conversationsTotal} total</span>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {conversationsLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading conversations...
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <History className="w-8 h-8 mb-2" />
              <p className="text-sm">No conversations yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {conversations.map(conv => (
                <div key={conv.session_id}>
                  <div className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                    onClick={() => loadConversationDetail(conv.session_id)}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {selectedConversation === conv.session_id
                        ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{conv.preview || 'Empty conversation'}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{conv.message_count} messages · {formatDate(conv.updated_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <button onClick={(e) => { e.stopPropagation(); deleteConversation(conv.session_id); }}
                        className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {selectedConversation === conv.session_id && conversationDetail && (
                    <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50/50">
                      <div className="flex justify-end gap-2 py-2">
                        <button onClick={() => continueConversation(conv)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 px-2 py-1 rounded hover:bg-indigo-50">
                          <ArrowRight className="w-3 h-3" /> Continue in Chat
                        </button>
                      </div>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {conversationDetail.messages.filter(m => m.role !== 'system').map((msg, i) => (
                          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] ${msg.role === 'user'
                              ? 'bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-3 py-2'
                              : 'bg-white border border-gray-200 text-gray-900 rounded-2xl rounded-tl-sm px-3 py-2'
                            }`}>
                              <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {conversationsTotal > 10 && (
          <div className="flex justify-center gap-2">
            <button onClick={() => fetchConversations(conversationPage - 1)}
              disabled={conversationPage <= 1}
              className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50">
              Previous
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-500">Page {conversationPage}</span>
            <button onClick={() => fetchConversations(conversationPage + 1)}
              disabled={conversations.length < 10}
              className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50">
              Next
            </button>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AdminTradosAgentHistory;
